/**
 * 站点配置上下文 - 提供全局站点配置信息
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSiteInfo as apiGetSiteInfo, type SiteInfo } from '@/utils/api';
import { getSiteNavMenus, getNavCategories } from '@/utils/api';
import { useSite } from './SiteContext';

interface NavMenuItem {
  id: string;
  name: string;
  type: string;
  target_id: string | null;
  target_url: string;
  sort_order: number;
  status: string;
}

interface NavCategory {
  id: string;
  name: string;
  slug: string;
  icon: string;
}

interface SiteConfigContextType {
  siteConfig: SiteInfo | null;
  navMenus: NavMenuItem[];
  navCategories: NavCategory[];
  loading: boolean;
  error: string | null;
  refreshConfig: () => void;
}

const SiteConfigContext = createContext<SiteConfigContextType>({
  siteConfig: null,
  navMenus: [],
  navCategories: [],
  loading: true,
  error: null,
  refreshConfig: () => {},
});

export function SiteConfigProvider({ children }: { children: React.ReactNode }) {
  const { siteId, loading: siteLoading, setSiteName } = useSite();
  const [siteConfig, setSiteConfig] = useState<SiteInfo | null>(null);
  const [navMenus, setNavMenus] = useState<NavMenuItem[]>([]);
  const [navCategories, setNavCategories] = useState<NavCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // 刷新配置的方法
  const refreshConfig = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  // 当站点ID或刷新键改变时，重新加载配置
  useEffect(() => {
    // 如果站点还在加载或没有 siteId，不发送请求
    if (siteLoading || !siteId) {
      return;
    }

    console.log('🔄 重新加载站点配置，siteId:', siteId, 'refreshKey:', refreshKey);

    setLoading(true);
    setError(null);

    Promise.all([
      apiGetSiteInfo().catch((e) => {
        console.error('获取站点信息失败:', e);
        return null;
      }),
      getSiteNavMenus().catch((e) => {
        console.error('获取导航菜单失败:', e);
        return [];
      }),
      getNavCategories().catch((e) => {
        console.error('获取导航分类失败:', e);
        return [];
      }),
    ]).then(([config, menus, categories]) => {
      console.log('✅ 站点配置加载成功:', config?.name, '菜单数量:', menus?.length);
      
      if (config) {
        setSiteConfig(config);
        // document.title 使用 SEO 标题，空则回退到 site_name -> name -> 空
        console.log('🔤 SEO标题:', config.seo_title, '网站名称:', config.site_name, '名称:', config.name);
        const pageTitle = config.seo_title || config.site_name || config.name || '';
        document.title = pageTitle;
        // 同步更新 SiteContext 的 siteName（使用基本设置中的网站名称）
        if (setSiteName) {
          const siteDisplayName = config.site_name || config.name || '';
          setSiteName(siteDisplayName);
          localStorage.setItem('current_site_name', siteDisplayName);
        }
      }
      
      if (menus && Array.isArray(menus)) {
        const menuLinks = (menus as NavMenuItem[])
          .filter((m: NavMenuItem) => m.status === 'active')
          .sort((a: NavMenuItem, b: NavMenuItem) => a.sort_order - b.sort_order);
        setNavMenus(menuLinks);
      }
      
      if (categories && Array.isArray(categories)) {
        setNavCategories(categories as NavCategory[]);
      }
    }).catch((e) => {
      console.error('加载站点配置失败:', e);
      setError('加载站点配置失败');
    }).finally(() => {
      setLoading(false);
    });
  }, [siteId, siteLoading, refreshKey]);

  return (
    <SiteConfigContext.Provider value={{ 
      siteConfig, 
      navMenus, 
      navCategories, 
      loading, 
      error,
      refreshConfig 
    }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}

export default SiteConfigContext;
